import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';
import Toast from '../components/Toast';
import VerifiedBadge from '../components/VerifiedBadge';

/**
 * AdminVerifications — /admin/verifications
 * Manual bio-code review. Admin opens the creator's social profile in a new tab,
 * eyeballs the bio for the unique code, then approves (grants verified badge) or
 * rejects (with optional reason). Both actions notify the user.
 */
const STATUS_TABS = [
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all',       label: 'All' },
];

export default function AdminVerifications() {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get('/admin/verifications', { params: { status, limit: 200 } });
      setRows(data.verifications || []);
      setCounts(data.counts || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load verifications');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const approve = async (v) => {
    if (busyId) return;
    setBusyId(v.id);
    try {
      await api.post(`/admin/verifications/${v.id}/approve`);
      setToast({ msg: `✓ Verified @${v.user.handle || v.user.email}` });
      fetchList();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Approve failed' });
    } finally { setBusyId(null); }
  };

  const openReject = (v) => { setRejectingId(v.id); setRejectReason(''); };
  const cancelReject = () => { setRejectingId(null); setRejectReason(''); };
  const confirmReject = async (v) => {
    if (busyId) return;
    setBusyId(v.id);
    try {
      const params = rejectReason.trim() ? { reason: rejectReason.trim() } : {};
      await api.post(`/admin/verifications/${v.id}/reject`, null, { params });
      setToast({ msg: 'Rejected & user notified.' });
      cancelReject();
      fetchList();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Reject failed' });
    } finally { setBusyId(null); }
  };

  const copyCode = async (v) => {
    try {
      await navigator.clipboard.writeText(v.bio_code);
      setCopiedId(v.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {}
  };

  return (
    <AdminLayout active="verifications">
      <div className="admin-page" data-testid="admin-verifications-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Verification queue</h1>
          <p className="admin-page-sub">
            Open the creator's social profile → confirm their bio contains the unique code →
            approve or reject. Both actions notify the user instantly.
          </p>
        </header>

        <div className="admin-toolbar">
          <div className="admin-chip-row">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`admin-chip${status === t.key ? ' active' : ''}`}
                onClick={() => setStatus(t.key)}
                data-testid={`vr-tab-${t.key}`}
              >
                {t.label}
                {counts[t.key] !== undefined && t.key !== 'all' && (
                  <span className="admin-chip-count">{counts[t.key]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="admin-error">{err}</div>}
        {loading && <div className="admin-loading">Loading…</div>}

        {!loading && !err && (
          rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-vr-empty">
              {status === 'pending'
                ? '🎉 No verifications waiting.'
                : `No ${status} verifications yet.`}
            </div>
          ) : (
            <div className="verify-review-grid" data-testid="admin-vr-grid">
              {rows.map((v) => (
                <div className={`verify-review-card verify-review-${v.status}`} key={v.id} data-testid={`vr-card-${v.id}`}>
                  <div className="verify-review-head">
                    <div className="verify-review-user">
                      <div className="verify-review-name">
                        {v.user.display_name || '—'}
                        {v.status === 'approved' && <VerifiedBadge size="sm" />}
                      </div>
                      <div className="admin-mono admin-small admin-muted">{v.user.email}</div>
                      <div className="admin-user-handle">@{v.clan_handle}</div>
                    </div>
                    <StatusChip status={v.status} />
                  </div>

                  <div className="verify-review-body">
                    <div className="verify-review-field">
                      <div className="verify-review-label">Platform</div>
                      <div className="verify-review-value">{v.platform_label}</div>
                    </div>
                    <div className="verify-review-field">
                      <div className="verify-review-label">Claimed handle</div>
                      <div className="verify-review-value">
                        <a
                          href={v.social_url}
                          target="_blank"
                          rel="noreferrer"
                          className="admin-inline-link"
                          data-testid={`vr-open-${v.id}`}
                        >
                          @{v.social_handle} ↗
                        </a>
                      </div>
                    </div>
                    <div className="verify-review-field verify-review-code-field">
                      <div className="verify-review-label">Bio code to look for</div>
                      <div className="verify-review-code">
                        <span data-testid={`vr-code-${v.id}`}>{v.bio_code}</span>
                        <button
                          type="button"
                          className="admin-btn-sm"
                          onClick={() => copyCode(v)}
                          data-testid={`vr-copy-${v.id}`}
                        >
                          {copiedId === v.id ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className="verify-review-field">
                      <div className="verify-review-label">Submitted</div>
                      <div className="verify-review-value admin-mono admin-small">{formatDate(v.submitted_at)}</div>
                    </div>
                    {v.reviewed_at && (
                      <div className="verify-review-field">
                        <div className="verify-review-label">Reviewed</div>
                        <div className="verify-review-value admin-mono admin-small">{formatDate(v.reviewed_at)}</div>
                      </div>
                    )}
                    {v.rejection_reason && (
                      <div className="verify-review-field verify-review-reason">
                        <div className="verify-review-label">Rejection reason</div>
                        <div className="verify-review-value">{v.rejection_reason}</div>
                      </div>
                    )}
                  </div>

                  {v.status === 'pending' && (
                    <div className="verify-review-actions">
                      {rejectingId === v.id ? (
                        <div className="verify-review-reject-panel">
                          <input
                            type="text"
                            className="admin-search"
                            placeholder="Reason (optional, shown to user)…"
                            value={rejectReason}
                            maxLength={280}
                            onChange={(e) => setRejectReason(e.target.value)}
                            data-testid={`vr-reject-reason-${v.id}`}
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              className="admin-btn-sm warn"
                              disabled={busyId === v.id}
                              onClick={() => confirmReject(v)}
                              data-testid={`vr-reject-confirm-${v.id}`}
                            >
                              {busyId === v.id ? '…' : 'Confirm reject'}
                            </button>
                            <button
                              type="button"
                              className="admin-btn-sm"
                              onClick={cancelReject}
                              data-testid={`vr-reject-cancel-${v.id}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="admin-btn-sm ok"
                            disabled={busyId === v.id}
                            onClick={() => approve(v)}
                            data-testid={`vr-approve-${v.id}`}
                          >
                            {busyId === v.id ? '…' : '✓ Approve'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn-sm warn"
                            disabled={busyId === v.id}
                            onClick={() => openReject(v)}
                            data-testid={`vr-reject-${v.id}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AdminLayout>
  );
}

function StatusChip({ status }) {
  const map = {
    pending:   { cls: 'admin-tag',          label: 'Pending' },
    approved:  { cls: 'admin-tag tag-ok',   label: 'Approved' },
    rejected:  { cls: 'admin-tag tag-warn', label: 'Rejected' },
    cancelled: { cls: 'admin-tag',          label: 'Cancelled' },
  };
  const it = map[status] || map.pending;
  return <span className={it.cls}>{it.label}</span>;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
